import { Checkbox, Row, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Table } from 'antd/lib';
import Text from 'components/Text';
import moment from 'moment';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import uiSelector from 'services/UI/selectors';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import { maskPhone } from 'utils/formatPhone';
import storage from 'utils/sessionStorage';
import { GIFT_CARDS } from '../pages';
import giftCardsActions from '../services/actions';
import { PATH_LOADING } from '../services/constants';
import giftCardsSelectors from '../services/selectors';
import { IGiftCard } from '../services/types/reducer';
import { IconStore, IconWeb } from './Icons';
import { TableGreenEvenStyled } from 'features/report/components/Component';
import clsx from 'clsx';
import ModalConfirm, { useModalConfirmRef } from 'components/Modal/ModalConfirm';
import giftCardsApis from '../services/apis';
import toast from 'utils/toast';
import { get } from 'lodash';
import { FeatureVerify, OTPVerificationRef } from 'widgets/OTPVerification';
import { verifyPinCode } from 'widgets/RequestAdminLocalPW/Callback';
type ITableGiftCardsProps = {
    resetTime?: number | undefined;
    searchText?: string | undefined;
    pageSize?: number | undefined;
    resetTimeChecked: string[];
    setResetTimeChecked: (value: any) => void;
    v2?: boolean;
    status: string;
};

const TableGiftCards: FC<ITableGiftCardsProps> = ({ v2, resetTime, searchText, pageSize, resetTimeChecked, setResetTimeChecked, status }) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const modalConfirm = useModalConfirmRef();
    const [data, setData] = useState<IGiftCard[]>([]);
    const currentPage = giftCardsSelectors.getCurrentPage();
    const listGiftCard = giftCardsSelectors.getListGiftCard();
    const totalElements = giftCardsSelectors.getTotalElements();
    const dataAppointment = giftCardsSelectors.getParams()?.[GIFT_CARDS];
    const loadingGiftCards = uiSelector.getLoading(PATH_LOADING.getListGiftCard);

    useEffect(() => {
        setData([...listGiftCard]);
    }, [listGiftCard]);

    const handleDelete = (record: IGiftCard) => {
        const cb = async () => {
            try {
                const res = await giftCardsApis.deleteGiftCard(record.id);
                if (res.data.data) {
                    const payload = {
                        page: currentPage + 1,
                        size: pageSize ?? 10,
                        resetNumber: resetTime ?? 0,
                        keySearch: searchText,
                        startDate: dataAppointment?.startTime,
                        endDate: dataAppointment?.endTime,
                        status: status === 'ACTIVE' ? true : false
                    };
                    toast.success('Delete gift card successfully');
                    dispatch(giftCardsActions.getListGiftCard.fetch(payload));
                } else {
                    toast.error('Delete gift card failed');
                }
            } catch (error) {
                const msg = get(error, 'response.data.message');
                toast.error(msg || 'Delete gift card failed');
            }
        };
        OTPVerificationRef.current?.openActiveSalon((valid) => {
            if (!valid) return verifyPinCode(() => cb());
            return cb();
        }, FeatureVerify.DELETE_GIFT_CARD);
    };

    const onRow = (data: IGiftCard) => {
        navigate(`/store/${storage.shop_id.get()}/gift-cards/${data.id}`);
    };

    const onResend = (data: IGiftCard) => {
        const cb = async () => {
            try {
                const res = await giftCardsApis.resendGiftCard(data?.id);
                if (res.data.data) {
                    toast.success('Resend gift card successfully');
                } else {
                    toast.error('Resend gift card failed');
                }
            } catch (error) {
                const msg = get(error, 'response.data.message');
                toast.error(msg || 'Resend gift card failed');
            }
        };
        modalConfirm.current?.open(`Do you want to resend gift card ${data?.giftCode || ''}?`, cb);
    };

    const columns: ColumnsType<IGiftCard> = [
        {
            title: 'Card number ',
            dataIndex: 'giftCode',
            align: v2 ? 'left' : 'center',
            width: v2 ? '250px' : undefined,
            onCell: () => {
                return {
                    onClick: event => {
                        event.stopPropagation(); // this will avoid onRow being called
                    }
                };
            },
            render(giftCode, record) {
                const isChecked = resetTimeChecked.findIndex(f => f == record.id) != -1;
                return (
                    <Checkbox checked={isChecked} onChange={() => {
                        if (isChecked) {
                            setResetTimeChecked(resetTimeChecked.filter(f => f != record.id));
                        } else {
                            setResetTimeChecked(resetTimeChecked.concat(record.id));
                        }
                    }} >
                        <EllipsisText className='card-number' ellipsis={{ tooltip: giftCode }}>
                            {giftCode || '--'}
                        </EllipsisText>
                    </Checkbox>
                );
            },
        },
        {
            title: 'Customer',
            dataIndex: 'customerName',
            align: v2 ? 'left' : 'center',
            render(customerName) {
                return (
                    <EllipsisText className='card-number' ellipsis={{ tooltip: customerName }}>
                        {customerName || '--'}
                    </EllipsisText>
                );
            },
        },
        // {
        //     title: 'Phone',
        //     dataIndex: 'customerPhone',
        //     align: v2 ? 'left' : 'center',
        //     render(customerPhone) {
        //         return (
        //             <Text variant="BODY_1" color="text_3">
        //                 {customerPhone ? maskPhone(customerPhone) : '--'}
        //             </Text>
        //         );
        //     },
        // },
        // {
        //     title: 'Staff',
        //     dataIndex: 'staffName',
        //     align: v2 ? 'left' : 'center',
        //     render(staffName) {
        //         return (
        //             <EllipsisText className='card-number' ellipsis={{ tooltip: staffName }}>
        //                 {staffName || '--'}
        //             </EllipsisText>
        //         );
        //     },
        // },
        {
            title: 'Amount',
            dataIndex: 'amount',
            align: v2 ? 'left' : 'center',
            width: v2 ? '150px' : undefined,
            render(amount) {
                return (
                    <Text variant="BODY_1" color="text_3">
                        {formatCurrency(amount)}
                    </Text>
                );
            },
        },
        {
            title: 'Redeemed',
            dataIndex: 'usedAmount',
            align: v2 ? 'left' : 'center',
            width: v2 ? '150px' : undefined,
            render(usedAmount) {
                return (
                    <Text variant="BODY_1" color="text_3">
                        {formatCurrency(usedAmount)}
                    </Text>
                );
            },
        },
        {
            title: 'Balance',
            dataIndex: 'balance',
            align: v2 ? 'left' : 'center',
            width: v2 ? '150px' : undefined,
            render(_, record) {
                const amount = record.amount ?? 0;
                const usedAmount = record.usedAmount ?? 0;
                const balance = amount - usedAmount;
                return (
                    <Text variant="BODY_1" color="text_3">
                        {formatCurrency(balance)}
                    </Text>
                );
            },
        },
        {
            title: 'Last Issued',
            dataIndex: 'updatedDate',
            align: v2 ? 'left' : 'center',
            width: v2 ? '150px' : undefined,
            render(updatedDate) {
                return (
                    <Text variant="BODY_1" color="text_3">
                        {updatedDate ? moment(updatedDate).format('MM-DD-YYYY') : ''}
                    </Text>
                );
            },
        },
        {
            title: 'Expiration Date',
            dataIndex: 'expireDate',
            align: v2 ? 'left' : 'center',
            width: v2 ? '150px' : undefined,
            render(expireDate) {
                return (
                    <Text variant="BODY_1" color="text_3">
                        {expireDate ? moment(expireDate).format('MM-DD-YYYY') : ''}
                    </Text>
                );
            },
        },
        {
            title: 'Source Type',
            dataIndex: 'buyOnline',
            align: v2 ? 'left' : 'center',
            width: v2 ? '200px' : undefined,
            render(buyOnline) {
                return (
                    <Row className='card-number' align={'middle'} style={{ gap: '5px' }}>
                        {buyOnline ? <IconWeb /> : <IconStore />}
                        {buyOnline ? 'Online' : 'In Store'}
                    </Row>
                );
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            align: v2 ? 'left' : 'center',
            width: v2 ? '200px' : undefined,
            render(status) {
                if (status) return <Text variant="CONTENT_2" color="info_3">ACTIVE</Text>;
                return <Text variant="CONTENT_2" color="info_1">INACTIVE</Text>;
            },
        },
        {
            title: 'Reset Time',
            dataIndex: 'numberReused',
            align: v2 ? 'left' : 'center',
            width: v2 ? '150px' : undefined,
            render(numberReused) {
                return (
                    <Text variant="BODY_1" color="text_3">
                        {numberReused}
                    </Text>
                );
            },
        },
        ...(status === 'ACTIVE' ? [
            {
                title: 'Action',
                dataIndex: 'action',
                width: v2 ? '150px' : undefined,
                render(_: any, record: IGiftCard) {
                return (
                    <ActionButtonContainer>
                        <ActionButton type='button' onClick={() => onRow(record)}>
                            Details
                        </ActionButton>
                        <ActionButton type='button' onClick={() => onResend(record)} className='resend'>
                            Resend
                        </ActionButton>
                        <ActionButton type='button' onClick={() => handleDelete(record)} className='delete'>
                            Delete
                        </ActionButton>
                    </ActionButtonContainer>
                    );
                },
        }
    ] : []),
    ];

    const handleChangePage = (page: number) => {
        const payload = {
            page: page,
            size: pageSize ?? 10,
            resetNumber: resetTime ?? 0,
            keySearch: searchText,
            startDate: dataAppointment?.startTime,
            endDate: dataAppointment?.endTime
        };
        dispatch(giftCardsActions.getListGiftCard.fetch(payload));
    };

    return (
        <TableGiftCardsStyled className={clsx('TableGiftCardsStyled', v2 && 'v2')}>
            <Table
                columns={columns}
                dataSource={data}
                rowKey={'id'}
                pagination={{
                    total: totalElements ?? 0,
                    pageSize: pageSize ?? 10,
                    current: currentPage + 1,
                    showSizeChanger: false,
                    onChange: handleChangePage
                }}
                // onRow={onRow}
                loading={loadingGiftCards}
            />
            <ModalConfirm ref={modalConfirm} />
        </TableGiftCardsStyled>
    );
};

export default TableGiftCards;
const TableGiftCardsStyled = styled(TableGreenEvenStyled)`
  padding: 0 1.5rem;
  margin-top: 1rem;

    .ant-table-tbody > tr > td {
        padding: 8px !important;
    }

    &.TableGiftCardsStyled.v2 {
        .ant-table-tbody .ant-table-cell,
        .ant-table-tbody .ant-table-cell .ant-typography {
            text-align: left;
        }
    }
  }

`;
const EllipsisText = styled(Typography.Text)`
  width: 6rem;
  color: var(--text-text-3, #1d2129);
  text-align: center;
  font-family: Open Sans;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;

const ActionButtonContainer = styled.div`
    display: flex;
    gap: 10px;
    justify-content: center;
    align-items: center;
`;

const ActionButton = styled.button`
    display: flex;
    height: 32px;
    padding: 16px 8px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 4px;
    flex: 1 0 0;
    border-radius: 4px;
    background: #e5e6eb;
    box-shadow: 0 2px 4px 0 rgba(44, 35, 66, 0.4), 0 4px 8px -3px rgba(45, 35, 66, 0.3), 0 -3px 0 0 #d6d6e7 inset;

    color: #1d2129;
    text-align: center;
    font-family: Poppins;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    &.resend {
        color: #fff;
        background: #6fabb6;
        box-shadow: 0 2px 4px 0 rgba(44, 35, 66, 0.4), 0 4px 8px -3px rgba(45, 35, 66, 0.3), 0 -4px 0 0 #144b55 inset;
    }
    &.delete {
        color: #fff;
        background: #ff8890;
        box-shadow: 0 2px 4px 0 rgba(44, 35, 66, 0.4), 0 4px 8px -3px rgba(45, 35, 66, 0.3), 0 -4px 0 0 #e7646d inset;
    }
`;
